import { useEffect } from 'react';

import type { SimpleBounds } from '@/types';
import type { Map as MapboxMap } from 'mapbox-gl';

import { ZOOM_8 } from '@/utils/constants/map';

interface UseTrainMapEventsProps {
  map: MapboxMap | null | undefined;
  visibleBoundsRef: { current: SimpleBounds | null };
  currentZoomRef: { current: number };
  isInteractingRef: { current: boolean };
  shouldForceRenderRef: { current: boolean };
  onCameraUpdate: () => void;
}

export const useTrainMapEvents = ({
  map,
  visibleBoundsRef,
  currentZoomRef,
  isInteractingRef,
  shouldForceRenderRef,
  onCameraUpdate,
}: UseTrainMapEventsProps) => {
  useEffect(() => {
    if (!map) {
      return;
    }

    let frameId: number | null = null;
    let interactionCount = 0;

    const updateBounds = () => {
      const b = map.getBounds();

      if (!b) {
        return;
      }

      currentZoomRef.current = map.getZoom();

      if (currentZoomRef.current < ZOOM_8) {
        shouldForceRenderRef.current = true;
      }

      const z = currentZoomRef.current;
      const padding = z < 9 ? 0.08 : z < 11 ? 0.05 : 0.01;

      visibleBoundsRef.current = {
        minX: b.getWest() - padding,
        maxX: b.getEast() + padding,
        minY: b.getSouth() - padding,
        maxY: b.getNorth() + padding,
      };

      onCameraUpdate();
    };

    const scheduleUpdateBounds = () => {
      if (frameId !== null) {
        return;
      }

      frameId = requestAnimationFrame(() => {
        frameId = null;
        updateBounds();
      });
    };

    const onStart = () => {
      interactionCount++;
      isInteractingRef.current = true;
    };

    const onEnd = () => {
      interactionCount = Math.max(0, interactionCount - 1);
      isInteractingRef.current = interactionCount > 0;
      updateBounds();
    };

    map.on('movestart', onStart);
    map.on('moveend', onEnd);
    map.on('zoomstart', onStart);
    map.on('zoomend', onEnd);
    map.on('styledata', scheduleUpdateBounds);
    map.on('resize', scheduleUpdateBounds);

    updateBounds();

    return () => {
      map.off('movestart', onStart);
      map.off('moveend', onEnd);
      map.off('zoomstart', onStart);
      map.off('zoomend', onEnd);
      map.off('styledata', scheduleUpdateBounds);
      map.off('resize', scheduleUpdateBounds);

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [
    map,
    visibleBoundsRef,
    currentZoomRef,
    isInteractingRef,
    shouldForceRenderRef,
    onCameraUpdate,
  ]);
};
